import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select/src/tom-select.popular'
import { TomInput, TomOption } from 'tom-select/src/types'
import { escape_html } from 'tom-select/src/utils'
import { isInViewport } from '../modules/utils'

interface UserTomOption extends TomOption {
  color: string
  text: string
}

export default class extends Controller {
  plugin: TomSelect
  defaultValue: string | string[] = ''
  resetHandler: () => void
  observer: IntersectionObserver

  connect (): void {
    if (isInViewport(this.element)) {
      // Load visible element immediately to avoid UI shift
      this.initializePlugin()
    } else {
      // Defer initialization of offscreen elements
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.observeWhenVisible(), { timeout: 10 })
      } else {
        setTimeout(() => this.observeWhenVisible(), 10)
      }
    }
  }

  disconnect (): void {
    if (!document.body.contains(this.element)) {
      if (this.plugin != null) {
        this.plugin.destroy()
      }
      if (this.observer != null) {
        this.observer.disconnect()
      }
      if (this.resetHandler != null) {
        this.element.closest('form')?.removeEventListener('reset', this.resetHandler)
      }
    }
  }

  private initializePlugin (): void {
    if (this.plugin != null) return

    this.plugin = new TomSelect(this.element as TomInput, {
      render: {
        option: (data: UserTomOption, escape: typeof escape_html) => (
          `<div class="option-flex"><div class="h-5 w-5 shadow rounded ${data.color}"></div><div>${escape(data.text)}</div></div>`
        ),
        item: (data: UserTomOption, escape: typeof escape_html) => (
          `<div class="item-flex"><div class="h-5 w-5 shadow rounded ${data.color}"></div><div class="text-primary">${escape(data.text)}</div></div>`
        )
      }
    })

    this.defaultValue = this.plugin.getValue() ?? ''

    this.resetHandler = () => {
      this.plugin.setValue(this.defaultValue, true)
    }

    this.element.closest('form')?.addEventListener('reset', this.resetHandler)

    this.hideLoader()
  }

  private hideLoader (): void {
    const wrapper = this.element.parentElement
    const loader = wrapper?.querySelector('[data-loader]')

    if (loader != null) loader.remove()
  }

  private observeWhenVisible (): void {
    if (this.plugin != null) return
    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.initializePlugin()
          this.observer.disconnect()
          break
        }
      }
    }, { rootMargin: '100px' })

    this.observer.observe(this.element)
  }
}
